import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAllWorkflows, Workflow } from '../services/workflowService';

const PromptManagement: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [analysisWorkflows, setAnalysisWorkflows] = useState<Workflow[]>([]);
  const [supportedTypes, setSupportedTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchWorkflows = async () => {
      try {
        const allWorkflows = await getAllWorkflows();
        if (cancelled) return;
        const nonTriageWorkflows = allWorkflows.filter(w => w.workflow_type !== 'triage');
        setAnalysisWorkflows(nonTriageWorkflows);

        const uniqueTypes = Array.from(new Set(
          nonTriageWorkflows.map(w => w.workflow_type)
        )).sort();
        setSupportedTypes(uniqueTypes);
      } catch (err) {
        console.error('Error fetching workflows:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchWorkflows();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="card">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
        Workflow Management
      </h2>

      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
          Supported Types:
        </h3>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
        ) : supportedTypes.length > 0 ? (
          <div className="flex flex-wrap gap-2 mb-4">
            {supportedTypes.map((type, index) => {
              const colors = [
                'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
                'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
                'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200',
                'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200',
                'bg-pink-100 dark:bg-pink-900 text-pink-800 dark:text-pink-200',
              ];
              const colorClass = colors[index % colors.length];
              
              return (
                <span 
                  key={type}
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${colorClass}`}
                >
                  {type}
                </span>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">No workflows configured yet</p>
        )}
      </div>

      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
          Custom Workflows:
        </h3>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading workflows...</p>
        ) : analysisWorkflows.length > 0 ? (
          <ul className="space-y-2">
            {analysisWorkflows.map((workflow) => (
              <li
                key={workflow.id}
                className="text-sm text-gray-700 dark:text-gray-300 flex items-center"
              >
                <svg className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                {workflow.name}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">No custom workflows yet</p>
        )}
      </div>

      <button
        onClick={() => history.push('/workflow-management')}
        className="btn-primary w-full"
      >
        Manage Workflows
      </button>
    </div>
  );
};

export default PromptManagement;
